// src/components/store/sections/product-cards/CurvedSliceProductCard.tsx
"use client";

import Link from "next/link";
import Image from "next/image";
import { ShoppingCart, Zap, Check, Loader2 } from "lucide-react";
import { UniversalProductCardProps, useCardActions } from "./useCardActions";

const ACCENT_PALETTE = [
  "#6C28FE",
  "#FF6A1A",
  "#10B981",
  "#0EA5E9",
  "#F59E0B",
  "#E11D48",
];

export default function CurvedSliceProductCard({
  product,
  index = 0,
  subdomain = "nextlook",
}: UniversalProductCardProps) {
  const {
    productImg,
    productName,
    productUrl,
    description,
    currentPrice,
    added,
    buying,
    handleAddToCart,
    handleBuyNow,
  } = useCardActions(product, subdomain);

  const cardAccent = ACCENT_PALETTE[index % ACCENT_PALETTE.length];
  const nameWords = productName.trim().split(" ");
  const firstLine = nameWords.slice(0, 1).join(" ");
  const secondLine = nameWords.slice(1, 3).join(" ") || "COLLECTION";

  return (
    <div className="relative w-full h-full bg-white rounded-[34px] p-5 shadow-[0_14px_35px_rgba(0,0,0,0.06)] hover:shadow-[0_24px_50px_rgba(0,0,0,0.12)] transition-all duration-500 flex flex-col justify-between border border-slate-100/90 group select-none overflow-hidden min-h-[420px]">
      {/* 1. Top Right Diagonal Color Slice */}
      <div
        className="absolute top-0 right-0 w-[72%] h-[58%] rounded-tr-[34px] overflow-hidden pointer-events-none transition-transform duration-500 group-hover:scale-[1.02] z-0"
        style={{
          backgroundColor: cardAccent,
          clipPath: "polygon(28% 0%, 100% 0%, 100% 88%, 0% 100%)",
        }}
      >
        <div className="absolute top-4 right-4 text-right font-sans">
          <span className="text-white text-xl sm:text-2xl font-black tracking-tight drop-shadow-md font-mono">
            ৳{Math.round(currentPrice).toLocaleString()}
          </span>
        </div>
      </div>

      {/* 2. Floating 3D Product Image */}
      <div className="relative w-full h-52 flex flex-col items-center justify-center my-1 z-20">
        <Link
          href={productUrl}
          className="w-full h-full flex flex-col items-center justify-center transition-all duration-500 group-hover:-translate-y-2 cursor-pointer"
        >
          <div className="relative w-40 h-40">
            <Image
              src={productImg}
              alt={productName}
              fill
              unoptimized
              className="object-contain drop-shadow-[0_20px_24px_rgba(0,0,0,0.38)] group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="w-32 h-3 mx-auto rounded-[100%] bg-black/25 blur-sm -mt-2" />
        </Link>
      </div>

      {/* 3. Title & Description */}
      <div className="mt-2 flex-1 flex flex-col justify-end relative z-10">
        <Link href={productUrl} className="block cursor-pointer">
          <h3
            className="text-base font-black uppercase tracking-wider leading-none truncate"
            style={{ color: cardAccent }}
          >
            {firstLine}
          </h3>
          <h4 className="text-base font-black uppercase tracking-wider text-slate-800 leading-tight truncate">
            {secondLine}
          </h4>
        </Link>

        <p className="text-[11px] text-slate-500 font-medium leading-relaxed mt-1.5 line-clamp-2">
          {description}
        </p>

        {/* 4. Action Buttons */}
        <div className="flex items-center gap-2 mt-4 relative z-20">
          <button
            type="button"
            onClick={(e) => handleBuyNow(e)}
            style={{ backgroundColor: cardAccent }}
            className="flex-1 py-2.5 px-3 rounded-full text-white font-black text-[10px] uppercase tracking-wider flex items-center justify-center gap-1 shadow-md hover:brightness-105 active:scale-95 transition-all cursor-pointer"
          >
            {buying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5 fill-current" />}
            <span>{buying ? "WAIT..." : "BUY NOW"}</span>
          </button>

          <button
            type="button"
            onClick={(e) => handleAddToCart(e)}
            className={`flex-1 py-2.5 px-3 rounded-full font-black text-[10px] uppercase tracking-wider flex items-center justify-center gap-1 transition-all active:scale-95 cursor-pointer shadow-md ${
              added ? "bg-emerald-600 text-white" : "bg-[#181a20] hover:bg-black text-white"
            }`}
          >
            {added ? (
              <>
                <Check className="w-3.5 h-3.5 text-white" />
                <span>ADDED</span>
              </>
            ) : (
              <>
                <ShoppingCart className="w-3.5 h-3.5 text-white" />
                <span>ADD CART</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
